import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

import DoubleDigitCountBubble from '../../DoubleDigitCountBubble';

const CellButton = styled.button<{ tint: 'white' | 'beige' }>`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 0 16px;
  border: 0;
  background: transparent;
  cursor: pointer;

  &.tint-white {
    border-right: 1px solid var(--stroke-light, #dddde2);
  }

  &.tint-beige {
    border-right: 1px solid var(--stroke-beige-light, #ccc9bc);
  }

  &:hover,
  &[aria-pressed='true'] {
    background: var(--background-light-blue, #eef2f9);
  }
`;

const AvatarWrap = styled.span`
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
`;

const AvatarImg = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
`;

const AvatarPlaceholder = styled.span`
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--brand-colors-beige-200, #f2f0ec);
`;

const Badge = styled(DoubleDigitCountBubble)`
  position: absolute;
  top: -6px;
  right: -10px;
  box-sizing: border-box;
  width: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border: 1px solid var(--stroke-beige-light, #ccc9bc);
  border-radius: var(--border-radius-radius-round, 104px);
  background: var(--error-dark, #b60000);
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: var(--text-white, #ffffff);
`;

const DropdownCue = styled.span`
  position: absolute;
  right: -7px;
  bottom: -4px;
  display: inline-flex;
  width: 18px;
  height: 18px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--brand-colors-beige-400, #ccc9bc);
  box-shadow: 0 0 0 2px var(--background-white, #ffffff);
  color: var(--text-primary, #141413);
  line-height: 1;
`;

export type AvatarCellProps = {
  avatarUrl: string;
  count: number;
  tint: 'white' | 'beige';
  pressed?: boolean;
  onClick?: () => void;
};

const AvatarCell = ({
  avatarUrl,
  count,
  tint,
  pressed,
  onClick,
}: AvatarCellProps) => {
  return (
    <CellButton
      type="button"
      tint={tint}
      aria-label="Account menu"
      aria-pressed={pressed ?? false}
      onClick={onClick}
      data-testid="avatar-cell"
    >
      <AvatarWrap>
        {avatarUrl !== '' ? (
          <AvatarImg src={avatarUrl} alt="" data-testid="avatar-img" />
        ) : (
          <AvatarPlaceholder data-testid="avatar-placeholder" />
        )}
        <Badge count={count} />
        <DropdownCue data-testid="avatar-dropdown-cue" aria-hidden>
          <Icon name="keyboard_arrow_down" size={18} />
        </DropdownCue>
      </AvatarWrap>
    </CellButton>
  );
};

export default AvatarCell;
